import { useState } from 'react';
import { Button, Dialog, Text, TextField } from 'frosted-ui';
import { api } from '../../services/api';
import { problemFrom } from '../ProblemCallout';
import type { Placement } from '../../types';
import { spotName } from '../../utils/paths';
import { cleanUsername, isValidUsername, USERNAME_RULE } from './StepPlacePartners';

export interface SpotManagement {
  experimentId: string;
  companyId: string;
  /** How many spots the test has: its last one can't go. */
  spotCount: number;
  /** Visitors a spot has had, when known. */
  visitsFor: (placementId: string) => number | null;
  onChanged: () => void;
}

/** What removing a spot does to its link, as the router treats it. */
export function removalEffect(placement: Placement, visits: number | null): string {
  if (placement.kind === 'in_whop') {
    return "Free members in that product stop seeing this test. Its results stay. Paywall Lab stays on the product until you take it off in Whop.";
  }
  if (visits === 0) {
    return 'Nobody has opened its link, so the link stops working. Take it down wherever you posted it.';
  }
  const anyVisits = visits === null ? 'If it has had visitors, its' : 'Its';
  const partner = placement.affiliateCode ? ' The partner stops getting credit for sales through it.' : '';
  return `${anyVisits} link keeps selling this test and its results stay, but new visitors no longer count under this link.${partner}`;
}

/** Rename and Remove for one spot on a test's results page. */
export default function SpotActions({
  placement,
  manage,
}: {
  placement: Placement;
  manage: SpotManagement;
}) {
  const [editing, setEditing] = useState(false);
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const [label, setLabel] = useState(placement.label);
  const [username, setUsername] = useState(placement.affiliateCode ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isPartner = placement.kind === 'partner';
  const canRename = placement.kind !== 'in_whop';
  const canRemove = !placement.isMain && manage.spotCount > 1;
  const name = spotName(placement);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const cleaned = cleanUsername(username);
      const usernameChanged = isPartner && cleaned !== (placement.affiliateCode ?? '');
      if (usernameChanged && cleaned) {
        const exists = await api.checkUsername(manage.companyId, cleaned);
        if (exists === false) {
          setError(`Whop has no user called “${cleaned}”. Check the spelling, or clear it.`);
          return;
        }
      }
      await api.updatePlacement(manage.experimentId, placement.id, {
        ...(label.trim() !== placement.label && { label: label.trim() }),
        ...(usernameChanged && { affiliateCode: cleaned }),
      });
      setEditing(false);
      manage.onChanged();
    } catch (err) {
      setError(problemFrom(err, "Couldn't save that. Try again.").message);
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    setSaving(true);
    setError(null);
    try {
      await api.removePlacement(manage.experimentId, placement.id);
      setConfirmingRemove(false);
      manage.onChanged();
    } catch (err) {
      setError(problemFrom(err, "Couldn't remove that. Try again.").message);
    } finally {
      setSaving(false);
    }
  }

  if (!canRename && !canRemove) return null;

  return (
    <div style={{ marginTop: 'var(--space-2)' }}>
      {!editing && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-4)' }}>
          {canRename && (
            <Button type="button" variant="ghost" color="gray" size="1" aria-label={`Rename ${name}`} onClick={() => setEditing(true)}>
              Rename
            </Button>
          )}
          {canRemove && (
            <Button type="button" variant="ghost" color="gray" size="1" aria-label={`Remove ${name}`} onClick={() => setConfirmingRemove(true)}>
              Remove
            </Button>
          )}
        </div>
      )}

      {editing && (
        <div className="vara2-step-in" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          <TextField.Root size="2" variant="surface">
            <TextField.Input
              aria-label="Link name"
              maxLength={80}
              value={label}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLabel(e.target.value)}
            />
          </TextField.Root>
          {isPartner && (
            <>
              <TextField.Root size="2" variant="surface" color={isValidUsername(username) ? undefined : 'red'}>
                <TextField.Input
                  aria-label="Their Whop username"
                  placeholder="Their Whop username (optional)"
                  maxLength={41}
                  value={username}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUsername(e.target.value)}
                />
              </TextField.Root>
              {!isValidUsername(username) && (
                <Text size="1" color="red">{USERNAME_RULE}</Text>
              )}
            </>
          )}
          <Text size="1" color="gray">The link stays the same.</Text>
          {error && <Text size="2" color="red" role="alert">{error}</Text>}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
            <Button
              type="button"
              size="2"
              variant="solid"
              loading={saving}
              disabled={saving || !label.trim() || !isValidUsername(username)}
              onClick={save}
            >
              Save
            </Button>
            <Button type="button" size="2" variant="soft" color="gray" disabled={saving} onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {confirmingRemove && (
        <Dialog.Root open onOpenChange={(open: boolean) => !open && !saving && setConfirmingRemove(false)}>
          <Dialog.Content style={{ maxWidth: 440 }}>
            <Dialog.Title size="4" style={{ marginBottom: 'var(--space-2)' }}>
              Remove {name}?
            </Dialog.Title>
            <Dialog.Description size="2" color="gray" style={{ display: 'block', marginBottom: 'var(--space-4)' }}>
              {removalEffect(placement, manage.visitsFor(placement.id))}
            </Dialog.Description>
            {error && (
              <Text size="2" color="red" role="alert" style={{ display: 'block', marginBottom: 'var(--space-3)' }}>
                {error}
              </Text>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-3)', justifyContent: 'flex-end' }}>
              <Button type="button" variant="soft" color="gray" size="2" disabled={saving} onClick={() => setConfirmingRemove(false)}>
                Cancel
              </Button>
              <Button type="button" variant="solid" color="red" size="2" loading={saving} disabled={saving} onClick={remove}>
                {placement.kind === 'in_whop' ? 'Remove from your whop' : 'Remove link'}
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Root>
      )}
    </div>
  );
}
